import { nextTick, ref, type Ref } from "vue";
import { loadLocalData, saveLocalData } from "@/BrowserDataStore";
import { executeLocalSaveRequest } from "@/DataLoaderStatic";
import { showSnackbar } from "@/UIHelper";

export interface PendingDienstplanSave {
  id: string;
  createdAt: number;
  title: string;
  request: any;
  previewEvent?: any;
}

export interface PendingDienstplanSavesDeps {
  dienstplan: () => boolean | undefined;
  query: () => string | undefined;
  queryMitarbeiter: () => string | undefined;
  rowdata: () => any[];
  employee: () => Array<{ GroupID: any; groupIDField: any }>;
  getRowMatchAic: (row: any) => number;
  dialog: { aic: any };
  goToAicLocal: { get: () => number; set: (value: number) => void };
  localDate: { set: (value: Date) => void };
  getCurrentEmployeeResourceId: () => any;
  getScheduleInstance: () => any;
  selectedDate: Ref<Date>;
  startHour: Ref<string>;
  startHourShow: string;
  eventSettings: Ref<any>;
  showDialog: Ref<boolean>;
  showRefresh: Ref<boolean>;
  showLoading?: Ref<boolean>;
  updateTimelineScrollbar?: () => void;
  refreshScheduleData: (skipRemount?: boolean) => Promise<void>;
  suppressAutoResourceScroll?: Ref<boolean>;
}

/**
 * Lokale "vorgemerkte" Dienstplan-Einträge, die noch nicht gespeichert wurden
 * (queue, localStorage-Persistenz, Vorschau-Events im Schedule, Fokus/Scroll beim Anlegen).
 * Extrahiert aus baseSyncSchedule.vue, siehe ai-context/vue-style-guide.md Abschnitt 6.
 */
export function usePendingDienstplanSaves(deps: PendingDienstplanSavesDeps) {
  const pendingDienstplanSaves = ref<PendingDienstplanSave[]>([]);
  const savingPendingDienstplan = ref<boolean>(false);
  const savingPendingDienstplanDone = ref<number>(0);
  const savingPendingDienstplanTotal = ref<number>(0);
  const pendingDienstplanFocusEvent = ref<any>(null);
  // einmaliger Trigger fürs Fokussieren: pendingDienstplanFocusEvent bleibt gesetzt bis zum
  // Speichern/Verwerfen, dataBound feuert aber laufend (zB beim Scrollen in gruppierter Ansicht) -
  // ohne dieses Flag würde dort bei jedem dataBound erneut zur Resource/zum Termin gescrollt
  // und die vertikale Scrollbar würde ständig zurückspringen.
  const shouldFocusPendingDienstplanOnDataBound = ref<boolean>(false);
  // Nach "Alle speichern" baut refreshScheduleData() die Schedule-Instanz per :key neu auf
  // (setCompKey) - die frische Instanz startet immer bei scrollTop/Left 0. Ein nachträgliches
  // setTimeout-Scrollen ist sichtbar (Sprung hoch, dann wieder runter). Stattdessen hier die
  // gewünschte Position hinterlegen - onDataBound (baseSyncSchedule.vue) wendet sie synchron
  // an, sobald die neue Instanz gebunden hat, noch bevor etwas gerendert/gemalt wird.
  const pendingScheduleScrollRestore = ref<{ scrollTop: number; scrollLeft: number } | null>(null);

  function isValidPendingPreviewDate(value: any) {
    const date = new Date(value);
    return !Number.isNaN(date.getTime()) && date.getFullYear() >= 2000;
  }

  function toPendingPreviewText(value: any): string {
    if (value === undefined || value === null || value === "") {
      return "";
    }
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    if (typeof value === "object") {
      const objectText =
        value.bezeichnung ??
        value.title ??
        value.text ??
        value.label ??
        value.name ??
        value.kennung ??
        value.display ??
        value.value;
      return objectText === value ? "" : toPendingPreviewText(objectText);
    }
    return "";
  }

  function normalizePendingPreviewEvent(previewEvent: any, request?: any) {
    if (!previewEvent) {
      return previewEvent;
    }

    const normalized = { ...previewEvent };
    const requestStart = request?.zeitbereich?.von;
    const requestEnd = request?.zeitbereich?.bis ?? requestStart;
    if (!isValidPendingPreviewDate(normalized.StartTime) && requestStart) {
      normalized.StartTime = new Date(requestStart);
      normalized.startDate = normalized.StartTime;
    }
    if (!isValidPendingPreviewDate(normalized.EndTime) && requestEnd) {
      normalized.EndTime = new Date(requestEnd);
    }
    if (
      isValidPendingPreviewDate(normalized.StartTime) &&
      (!isValidPendingPreviewDate(normalized.EndTime) ||
        new Date(normalized.EndTime).getTime() <= new Date(normalized.StartTime).getTime())
    ) {
      normalized.EndTime = new Date(normalized.StartTime);
      normalized.EndTime.setHours(normalized.EndTime.getHours() + 1);
    }

    if (!normalized.GroupID) {
      normalized.GroupID =
        deps.dialog.aic ?? deps.goToAicLocal.get() ?? deps.getCurrentEmployeeResourceId();
    }

    if (normalized.GroupID) {
      normalized.idField = normalized.GroupID;
      let employeeResource = deps.employee().find(
        (item) => String(item.GroupID) === String(normalized.GroupID)
      );
      if (!employeeResource) {
        const currentResourceId = deps.getCurrentEmployeeResourceId();
        employeeResource = deps.employee().find(
          (item) => String(item.GroupID) === String(currentResourceId)
        );
      }
      if (employeeResource) {
        normalized.GroupID = employeeResource.GroupID;
        normalized.idField = employeeResource.GroupID;
        normalized.id = employeeResource.groupIDField;
      }
    }

    return normalized;
  }

  function getPendingDienstplanPreviewEvents() {
    if (!deps.dienstplan()) {
      return [];
    }

    return pendingDienstplanSaves.value
      .filter((entry) => entry.previewEvent)
      .map((entry) => {
        const previewEvent = normalizePendingPreviewEvent(entry.previewEvent, entry.request);
        return {
          ...previewEvent,
          Subject: toPendingPreviewText(previewEvent?.Subject || entry.title),
          IcsSubject: toPendingPreviewText(previewEvent?.IcsSubject || previewEvent?.Subject || entry.title),
          Location: toPendingPreviewText(previewEvent?.Location),
          LocationText: toPendingPreviewText(previewEvent?.LocationText),
          Description: toPendingPreviewText(previewEvent?.Description),
          Description2: toPendingPreviewText(previewEvent?.Description2),
          pendingDienstplanSaveId: entry.id,
          Id: `local_${entry.id}`,
          aic_Bew_pool: previewEvent?.aic_Bew_pool ?? `local_${entry.id}`,
          isLocalPendingDienstplan: true,
        };
      });
  }

  function getScheduleDataSource() {
    if (!deps.dienstplan()) {
      return deps.rowdata();
    }

    // bereits gespeicherte Einträge, die gerade lokal überarbeitet werden, aus den
    // "echten" Serverdaten ausblenden - sonst würde der alte (unveränderte) Eintrag
    // zusätzlich zur lokalen Vormerkung angezeigt werden ("zweiter Termin")
    const editedAicBewPools = new Set(
      pendingDienstplanSaves.value
        .map((entry) => Number(entry.request?.aicBewPool))
        .filter((aic) => Number.isFinite(aic) && aic !== 0)
    );
    const visibleRowdata = editedAicBewPools.size === 0
      ? deps.rowdata()
      : deps.rowdata().filter((row: any) => !editedAicBewPools.has(deps.getRowMatchAic(row)));

    return [...visibleRowdata, ...getPendingDienstplanPreviewEvents()];
  }

  function refreshScheduleDataSourceWithPending() {
    // eventSettings ist reaktiv an :eventSettings gebunden - der EJ2-Vue-Wrapper bindet die
    // Schedule-Instanz darüber bereits neu (setProperties -> dataBind). Ein zusätzliches
    // manuelles scheduleInstance.eventSettings.dataSource = ...; dataBind(); refreshEvents()
    // umgeht diesen Update-Pfad und bindet dieselben Daten kurz danach ein zweites Mal (bzw.
    // mit dem 120ms-Timeout sogar ein drittes Mal) - dadurch geriet die interne
    // Virtual-Scroll-Buchführung durcheinander und alte Termin-Elemente blieben stehen
    // ("Termin erscheint doppelt" nach dem Bearbeiten einer lokalen Vormerkung).
    deps.eventSettings.value = {
      ...deps.eventSettings.value,
      dataSource: getScheduleDataSource(),
    };

    nextTick(() => {
      deps.updateTimelineScrollbar?.();
    });
  }

  function closeQueuedDienstplanDialog() {
    deps.showDialog.value = false;
    deps.showRefresh.value = false;
  }

  function scrollPendingDienstplanEventIntoView(scheduleElement?: Element | null) {
    const scheduleDok = scheduleElement ?? document.querySelector(".e-schedule");
    const contentWrap = scheduleDok?.querySelector(".e-content-wrap") as HTMLElement | null;
    const focusedPendingId = pendingDienstplanFocusEvent.value?.pendingDienstplanSaveId;
    const hasFocusTarget = Boolean(pendingDienstplanFocusEvent.value);
    const localAppointments = Array.from(
      scheduleDok?.querySelectorAll(".dienstplan-local-pending-event") ?? []
    ) as HTMLElement[];
    let localAppointment = focusedPendingId
      ? localAppointments.find(
        (element) => element.dataset.pendingDienstplanSaveId === String(focusedPendingId)
      ) ?? null
      : null;

    if (!localAppointment) {
      localAppointment = scheduleDok?.querySelector(
        ".dienstplan-local-pending-event-focus"
      ) as HTMLElement | null;
    }

    if (!localAppointment && !hasFocusTarget) {
      localAppointment = localAppointments[0] ?? null;
    }

    if (!contentWrap) {
      return false;
    }

    if (!localAppointment && hasFocusTarget) {
      return scrollPendingDienstplanDateIntoView();
    }

    if (!localAppointment) {
      return false;
    }

    const wrapRect = contentWrap.getBoundingClientRect();
    const appointmentRect = localAppointment.getBoundingClientRect();

    // Termin ist bereits vollständig sichtbar - Position bleibt wie sie war, kein
    // erzwungenes Zentrieren (sonst "springt" die Ansicht auch wenn sie schon passt).
    const alreadyVisible =
      appointmentRect.top >= wrapRect.top &&
      appointmentRect.bottom <= wrapRect.bottom &&
      appointmentRect.left >= wrapRect.left &&
      appointmentRect.right <= wrapRect.right;
    if (alreadyVisible) {
      return true;
    }

    contentWrap.scrollLeft +=
      appointmentRect.left -
      wrapRect.left -
      wrapRect.width / 2 +
      appointmentRect.width / 2;
    contentWrap.scrollTop +=
      appointmentRect.top -
      wrapRect.top -
      wrapRect.height / 2 +
      appointmentRect.height / 2;
    contentWrap.dispatchEvent(new Event("scroll"));
    deps.updateTimelineScrollbar?.();
    return true;
  }

  /** Fallback solange das Vorschau-Element noch nicht gerendert ist: Syncfusion direkt zum Datum scrollen lassen. */
  function scrollPendingDienstplanDateIntoView() {
    const focusDate = new Date(
      pendingDienstplanFocusEvent.value?.StartTime ?? pendingDienstplanFocusEvent.value?.startDate
    );
    if (Number.isNaN(focusDate.getTime()) || focusDate.getFullYear() < 2000) {
      return false;
    }

    const scheduleInstance = deps.getScheduleInstance();
    if (typeof scheduleInstance?.scrollTo !== "function") {
      return false;
    }

    scheduleInstance.scrollTo(deps.startHour.value || deps.startHourShow, focusDate);
    deps.updateTimelineScrollbar?.();
    return true;
  }

  function getMondayOfWeek(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(d.getFullYear(), d.getMonth(), diff);
  }

  function retryScrollPendingDienstplanEventIntoView(
    scheduleElement?: Element | null,
    attempts = 4
  ) {
    const currentScheduleElement = scheduleElement?.isConnected ? scheduleElement : null;
    const found = scrollPendingDienstplanEventIntoView(currentScheduleElement);
    // sobald der Termin gefunden wurde (egal ob schon sichtbar oder gerade zentriert),
    // erübrigen sich weitere Versuche - sonst läuft die Kette unnötig weiter und die
    // Ansicht wackelt/springt bei jedem Retry erneut.
    if (found || attempts <= 1) {
      return;
    }

    window.setTimeout(
      () => retryScrollPendingDienstplanEventIntoView(currentScheduleElement, attempts - 1),
      120
    );
  }

  function focusPendingDienstplanEvent(previewEvent: any) {
    if (!previewEvent) {
      return;
    }

    pendingDienstplanFocusEvent.value = previewEvent;
    const startTime = new Date(previewEvent.StartTime ?? previewEvent.startDate);
    const hasValidFocusDate = !Number.isNaN(startTime.getTime()) && startTime.getFullYear() >= 2000;
    // Ansicht beim Anlegen nie umschalten: selectedDate.value = startTime würde intern
    // "navigating" auslösen (Wochenwechsel, Re-Render) - der Nutzer soll auf der aktuell
    // sichtbaren Woche/Position bleiben, auch wenn der neue Eintrag außerhalb liegt.
    const staysInSameWeek =
      hasValidFocusDate &&
      deps.selectedDate.value &&
      getMondayOfWeek(deps.selectedDate.value).getTime() === getMondayOfWeek(startTime).getTime();
    if (hasValidFocusDate) {
      deps.localDate.set(startTime);
    }

    const resourceId = previewEvent.GroupID ?? previewEvent.idField;
    if (resourceId) {
      deps.goToAicLocal.set(resourceId);
    }

    if (!staysInSameWeek) {
      // Termin liegt außerhalb der aktuell angezeigten Woche - ohne Wochenwechsel gibt es
      // nichts zum Zentrieren/Scrollen (Element existiert im aktuellen DOM gar nicht).
      return;
    }
    shouldFocusPendingDienstplanOnDataBound.value = true;

    nextTick(() => {
      window.setTimeout(() => {
        const scheduleDok = document.querySelector(".e-schedule");
        const scheduleInstance = (scheduleDok as any)?.ej2_instances?.[0];
        if (!scheduleInstance) {
          return;
        }

        // scrollToResource() richtet die Resource-Zeile am oberen Rand aus, während
        // retryScrollPendingDienstplanEventIntoView() den Termin zentriert - beide
        // hintereinander erzeugen einen sichtbaren Sprung. Zentrieren reicht allein.
        retryScrollPendingDienstplanEventIntoView(scheduleDok, 8);

      }, 240);
    });
  }

  function getDienstplanPendingSaveStorageKey() {
    return [
      "baseSyncScheduleDienstplanPending",
      deps.query() ?? "default",
      deps.queryMitarbeiter() ?? "default",
    ].join(":");
  }

  function loadPendingDienstplanSaves() {
    if (!deps.dienstplan()) {
      return;
    }

    pendingDienstplanSaves.value = loadLocalData(
      getDienstplanPendingSaveStorageKey(),
      []
    );
  }

  function persistPendingDienstplanSaves() {
    if (!deps.dienstplan()) {
      return;
    }

    saveLocalData(
      getDienstplanPendingSaveStorageKey(),
      pendingDienstplanSaves.value
    );
  }

  function queueDienstplanSave(payload: { title?: string; request: any; previewEvent?: any; replaceId?: string }) {
    if (!deps.dienstplan() || !payload?.request) {
      return;
    }

    if (payload.replaceId) {
      // bearbeitete lokale Vormerkung ersetzen statt zusätzlich anzuhängen
      const replaceIdx = pendingDienstplanSaves.value.findIndex(
        (entry) => entry.id === payload.replaceId
      );
      if (replaceIdx >= 0) {
        pendingDienstplanSaves.value.splice(replaceIdx, 1);
      }
    }

    const pendingId = `${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const previewEvent = normalizePendingPreviewEvent({
      ...payload.previewEvent,
      GroupID:
        payload.previewEvent?.GroupID ??
        deps.dialog.aic ??
        deps.goToAicLocal.get() ??
        deps.getCurrentEmployeeResourceId(),
    }, payload.request);
    const pendingPreviewEvent = {
      ...previewEvent,
      pendingDienstplanSaveId: pendingId,
    };
    pendingDienstplanSaves.value.push({
      id: pendingId,
      createdAt: Date.now(),
      title: payload.title || "Dienstplan-Eintrag",
      request: payload.request,
      previewEvent: pendingPreviewEvent,
    });
    persistPendingDienstplanSaves();
    refreshScheduleDataSourceWithPending();
    focusPendingDienstplanEvent(pendingPreviewEvent);
    showSnackbar({
      text: "Dienstplan-Eintrag wurde lokal vorgemerkt.",
      color: "info",
    });
  }

  function discardPendingDienstplanSaves() {
    pendingDienstplanSaves.value = [];
    persistPendingDienstplanSaves();
    refreshScheduleDataSourceWithPending();
    showSnackbar({
      text: "Lokale Dienstplan-Vormerkungen wurden verworfen.",
      color: "info",
    });
  }

  function discardPendingDienstplanSave(id: string, abwAicAbwesenheiten?: number) {
    let idx = pendingDienstplanSaves.value.findIndex((entry) => entry.id === id);
    if (idx < 0 && abwAicAbwesenheiten) {
      // id passt nicht (zB. veraltete/erneut vergebene pending-ID) -
      // über die Abwesenheits-Bewegungs-ID nachsuchen
      idx = pendingDienstplanSaves.value.findIndex(
        (entry) =>
          Number(entry.previewEvent?.abwAicAbwesenheiten) === Number(abwAicAbwesenheiten)
      );
    }
    if (idx < 0) {
      return;
    }
    const removedId = pendingDienstplanSaves.value[idx].id;
    pendingDienstplanSaves.value.splice(idx, 1);
    persistPendingDienstplanSaves();
    refreshScheduleDataSourceWithPending();
    if (pendingDienstplanFocusEvent.value?.pendingDienstplanSaveId === removedId) {
      pendingDienstplanFocusEvent.value = null;
    }
    showSnackbar({
      text: "Lokale Dienstplan-Vormerkung wurde verworfen.",
      color: "info",
    });
  }

  function captureScheduleScrollPosition() {
    const contentWrap = document.querySelector(".e-schedule .e-content-wrap") as HTMLElement | null;
    if (!contentWrap) {
      return null;
    }
    return { scrollTop: contentWrap.scrollTop, scrollLeft: contentWrap.scrollLeft };
  }

  /**
   * Führt einen Reload aus (reloadData()/refreshScheduleData()/...), ohne die aktuelle
   * Scroll-Position zu verlieren. Jeder solche Reload baut die Schedule-Instanz per :key
   * neu auf (setCompKey in fillEvents()) - die frische Instanz startet immer bei
   * Scroll-Position 0. Verwendet von savePendingDienstplanSaves() und von
   * baseSyncSchedule.vue für onDialogSave()/dialogBack() (zB nach dem Löschen eines
   * bereits gespeicherten Termins).
   */
  async function reloadPreservingScroll(reloadFn: () => Promise<void>) {
    const scrollPositionBefore = captureScheduleScrollPosition();
    if (deps.suppressAutoResourceScroll) {
      deps.suppressAutoResourceScroll.value = true;
    }
    // Erst unmittelbar vor dem eigentlichen Remount die Zielposition hinterlegen -
    // onDataBound (baseSyncSchedule.vue) wendet sie synchron auf die neue Instanz an.
    pendingScheduleScrollRestore.value = scrollPositionBefore;
    await reloadFn();
    if (deps.suppressAutoResourceScroll) {
      // Deckt denselben Zeitraum ab wie scrollRestoreSuppressUntil (baseSyncSchedule.vue,
      // 1000ms) - fillUpEvents() ruft in seinem eigenen finally-Block per nextTick +
      // setTimeout(120ms) noch verzögert scrollToResource() zur zuletzt gemerkten Resource
      // auf; ohne dieses Fenster würde das die gerade wiederhergestellte Position erneut
      // verschieben.
      window.setTimeout(() => {
        deps.suppressAutoResourceScroll!.value = false;
      }, 1000);
    }
  }

  async function savePendingDienstplanSaves() {
    if (savingPendingDienstplan.value || pendingDienstplanSaves.value.length === 0) {
      return;
    }

    savingPendingDienstplan.value = true;
    // Reload-Anzeige (Toolbar-Sync-Icon, Lade-Leiste) sofort beim Klick zeigen, nicht erst
    // wenn refreshScheduleData() am Ende der Schleife anläuft - sonst wirkt der Button
    // zwischenzeitlich reaktionslos, bis der erste Request durch ist.
    if (deps.showLoading) {
      deps.showLoading.value = true;
    }
    const initialCount = pendingDienstplanSaves.value.length;
    savingPendingDienstplanDone.value = 0;
    savingPendingDienstplanTotal.value = initialCount;
    try {
      while (pendingDienstplanSaves.value.length > 0) {
        const entry = pendingDienstplanSaves.value[0];
        await executeLocalSaveRequest(entry.request);
        // absichtlich KEIN refreshScheduleDataSourceWithPending() hier: die lokalen
        // Vorschau-Termine sollen sichtbar bleiben, solange noch nicht ALLE Einträge
        // bestätigt sind - sonst verschwindet jeder Termin einzeln aus der Ansicht, bevor
        // er (erst am Ende, per refreshScheduleData()) durch den echten Termin ersetzt
        // wird ("flackert" pro Eintrag). shift()/persist() nur fürs interne Tracking
        // (Fortschritt, kein doppeltes Senden bei einem Fehler mitten in der Schleife).
        pendingDienstplanSaves.value.shift();
        persistPendingDienstplanSaves();
        savingPendingDienstplanDone.value += 1;
      }

      showSnackbar({
        text: `${initialCount} Dienstplan-Eintrag${initialCount === 1 ? "" : "e"} gespeichert.`,
        color: "success",
      });
      pendingDienstplanFocusEvent.value = null;
      deps.showRefresh.value = true;
      // skipRemount=true: nach dem Speichern reicht ein reines Datenrefresh (eventSettings/
      // rowdata in place aktualisieren) - kein voller Remount der Schedule-Instanz nötig,
      // genau wie bei deleteRow() (baseSyncSchedule.vue). Vermeidet sowohl den Sprung nach
      // oben als auch das kurze Aus-/Wiedereinblenden der ganzen Ansicht ("flackert").
      await reloadPreservingScroll(() => deps.refreshScheduleData(true));
    } catch (ex: any) {
      const text = ex?.response?.data?.error || ex?.response?.data?.info || ex?.message || ex;
      showSnackbar({
        text: `Speichern abgebrochen: ${text}`,
        color: "error",
        duration: 6000,
      });
    } finally {
      savingPendingDienstplan.value = false;
      savingPendingDienstplanDone.value = 0;
      savingPendingDienstplanTotal.value = 0;
    }
  }

  return {
    pendingDienstplanSaves,
    savingPendingDienstplan,
    savingPendingDienstplanDone,
    savingPendingDienstplanTotal,
    pendingDienstplanFocusEvent,
    shouldFocusPendingDienstplanOnDataBound,
    pendingScheduleScrollRestore,
    reloadPreservingScroll,
    getPendingDienstplanPreviewEvents,
    getScheduleDataSource,
    refreshScheduleDataSourceWithPending,
    scrollPendingDienstplanEventIntoView,
    retryScrollPendingDienstplanEventIntoView,
    closeQueuedDienstplanDialog,
    focusPendingDienstplanEvent,
    normalizePendingPreviewEvent,
    loadPendingDienstplanSaves,
    queueDienstplanSave,
    discardPendingDienstplanSaves,
    discardPendingDienstplanSave,
    savePendingDienstplanSaves,
  };
}
